import * as React from 'react';

import { enterFullscreen, exitFullscreen } from './fullscreen-helpers';

export const useFullscreen = (
  videoRef: React.RefObject<HTMLVideoElement | null>,
  containerRef: React.RefObject<HTMLDivElement | null>
) => {
  const [isFullscreen, setIsFullscreen] = React.useState(false);

  const toggleFullscreen = () => {
    if (!containerRef.current || !videoRef.current) return;

    if (isFullscreen) {
      exitFullscreen();
    } else {
      enterFullscreen(videoRef.current, containerRef.current);
    }
  };

  React.useEffect(() => {
    const handleFullscreenChange = () => {
      // Check container fullscreen state with vendor prefixes
      const doc = document as Document & {
        mozFullScreenElement?: Element;
        webkitFullscreenElement?: Element;
        msFullscreenElement?: Element;
      };

      const fullscreenElement =
        doc.fullscreenElement ??
        doc.webkitFullscreenElement ??
        doc.mozFullScreenElement ??
        doc.msFullscreenElement;

      // Check video element fullscreen state (for mobile devices)
      const video = videoRef.current as HTMLVideoElement & {
        webkitDisplayingFullscreen?: boolean;
      };

      const isVideoFullscreen = video?.webkitDisplayingFullscreen ?? false;

      setIsFullscreen(!!fullscreenElement || isVideoFullscreen);
    };

    // Add all vendor-prefixed fullscreen change event listeners
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    // Add video fullscreen event listeners
    const video = videoRef.current;
    if (video) {
      video.addEventListener('webkitbeginfullscreen', handleFullscreenChange);
      video.addEventListener('webkitendfullscreen', handleFullscreenChange);
    }

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener(
        'webkitfullscreenchange',
        handleFullscreenChange
      );
      document.removeEventListener(
        'mozfullscreenchange',
        handleFullscreenChange
      );
      document.removeEventListener(
        'MSFullscreenChange',
        handleFullscreenChange
      );

      if (video) {
        video.removeEventListener(
          'webkitbeginfullscreen',
          handleFullscreenChange
        );
        video.removeEventListener(
          'webkitendfullscreen',
          handleFullscreenChange
        );
      }
    };
  }, [videoRef]);

  return { isFullscreen, toggleFullscreen };
};
